import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Chip, Stack, Tooltip, Divider } from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  CalendarToday as CalendarIcon,
  Percent as PercentIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  SvgIconComponent
} from '@mui/icons-material';
import { Hotel, OwnerDetailsTabProps } from '@types';

const OwnerDetailsTab: React.FC<OwnerDetailsTabProps> = ({ hotel }) => {
  const renderInfoChip = (Icon: SvgIconComponent, label: string, tooltip: string, color: "primary" | "secondary" | "info" | "success" | "warning" | "error" = "primary") => (
    <Tooltip title={tooltip} arrow>
      <Chip
        icon={<Icon />}
        label={label}
        variant="filled"
        color={color}
        sx={{
          fontWeight: 500,
          px: 1,
          '& .MuiChip-icon': { fontSize: '1.2rem' },
        }}
      />
    </Tooltip>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
        <PersonIcon sx={{ mr: 1 }} />
        Owner Information
      </Typography>

      {/* Owner Information Section */}
      {hotel.owner_name || hotel.owner_entity || hotel.owner_contact_name ? (
        <Grid container spacing={3}>
          {/* Owner Key Information */}
          <Grid item xs={12}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <VerifiedUserIcon sx={{ mr: 1 }} />
                Key Information
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                {hotel.owner_name && renderInfoChip(
                  PersonIcon,
                  hotel.owner_name,
                  'Owner Name',
                  'primary'
                )}
                {hotel.owner_entity && renderInfoChip(
                  BusinessIcon,
                  hotel.owner_entity,
                  'Owner Entity/Company',
                  'secondary'
                )}
                {hotel.legal_entity_type && renderInfoChip(
                  BusinessIcon,
                  hotel.legal_entity_type,
                  'Legal Entity Type',
                  'info'
                )}
                {hotel.ownership_percentage && renderInfoChip(
                  PercentIcon,
                  `${hotel.ownership_percentage}% Ownership`,
                  'Ownership Percentage',
                  'success'
                )}
              </Stack>
            </Box>
          </Grid>

          {/* Owner Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              Owner Details
            </Typography>
            <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
              <Grid container spacing={3}>
                {hotel.owner_name && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Owner Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{hotel.owner_name}</Typography>
                  </Grid>
                )}

                {hotel.owner_entity && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Entity/Company
                    </Typography>
                    <Typography variant="body1">{hotel.owner_entity}</Typography>
                  </Grid>
                )}

                {hotel.legal_entity_type && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Legal Entity Type
                    </Typography>
                    <Typography variant="body1">{hotel.legal_entity_type}</Typography>
                  </Grid>
                )}

                {hotel.ownership_structure && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Ownership Structure
                    </Typography>
                    <Typography variant="body1">{hotel.ownership_structure}</Typography>
                  </Grid>
                )}

                {hotel.ownership_percentage && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Ownership Percentage
                    </Typography>
                    <Typography variant="body1">{hotel.ownership_percentage}%</Typography>
                  </Grid>
                )}

                {hotel.owner_since && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Owner Since
                    </Typography>
                    <Typography variant="body1">{new Date(hotel.owner_since).toLocaleDateString()}</Typography>
                  </Grid>
                )}

                {hotel.acquisition_date && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Acquisition Date
                    </Typography>
                    <Typography variant="body1">{new Date(hotel.acquisition_date).toLocaleDateString()}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Owner Contact Information */}
          {(hotel.owner_contact_name || hotel.owner_contact_email || hotel.owner_contact_phone) && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1 }} />
                Contact Information
              </Typography>
              <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
                <Grid container spacing={3}>
                  {hotel.owner_contact_name && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Contact Person
                      </Typography>
                      <Typography variant="body1">{hotel.owner_contact_name}</Typography>
                    </Grid>
                  )}

                  {hotel.owner_contact_email && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        component="a"
                        href={`mailto:${hotel.owner_contact_email}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {hotel.owner_contact_email}
                      </Typography>
                    </Grid>
                  )}

                  {hotel.owner_contact_phone && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Phone
                      </Typography>
                      <Typography
                        variant="body1"
                        component="a"
                        href={`tel:${hotel.owner_contact_phone}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {hotel.owner_contact_phone}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Owner Address */}
          {(hotel.owner_address || hotel.owner_city || hotel.owner_state) && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1 }} />
                Owner Address
              </Typography>
              <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {[
                        hotel.owner_address,
                        hotel.owner_city,
                        hotel.owner_state,
                        hotel.owner_zip,
                        hotel.owner_country
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Additional Owner Details */}
          {(hotel.ownership_type_details || hotel.ownership_notes || hotel.tax_id) && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} />
                Additional Details
              </Typography>
              <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                <Grid container spacing={3}>
                  {hotel.tax_id && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Tax ID
                      </Typography>
                      <Typography variant="body1">{hotel.tax_id}</Typography>
                    </Grid>
                  )}

                  {hotel.ownership_type_details && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ownership Type Details
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {hotel.ownership_type_details}
                      </Typography>
                    </Grid>
                  )}

                  {hotel.ownership_notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ownership Notes
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {hotel.ownership_notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mt: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          No owner details have been added for this hotel yet. Click the "Edit Hotel" button to add owner information.
        </Alert>
      )}
    </Paper>
  );
};

export default OwnerDetailsTab;
